import type { z } from 'zod'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import {
  type ApiInputToken,
  type MarketForRewards,
  type MarketRewards,
  parseApiAsset,
  type RewardMarket,
  type Rewards,
  type RewardsMapByToken,
  type VaultRewards
} from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'
import { getAddress } from 'viem'

import type { CollateralRewardEntry } from '@/components/common/CollateralYieldTags'
import type { vaultApyRewardSchema } from '@/lib/orpc/schemas'
import type { MarketApyReward } from '@/lib/procedures/marketApy.contract'

import { getParsedValue } from './formatters'
import { sortingOnKey } from './sorting'

export const normalizeRewards = (
  vaultStateRewards: readonly (MarketRewards | VaultRewards)[] | null | undefined
) => {
  const vaultStateRewardsMap = getRewardsFromVaultRewards(vaultStateRewards)
  const sortedRewards = sortingOnKey('apr', Array.from(vaultStateRewardsMap.tokens.values()))

  return sortedRewards.filter(reward => reward.apr && reward.apr > 0n)
}

type ApyRewardRow = {
  token: MarketApyReward['token']
  apr: number
  merklOpportunityUrl: string | null
}

/**
 * One hover-card reward per token with its APRs summed and the first Merkl campaign URL kept,
 * sorted by APR, zero APRs dropped. A reward token's decimals are never displayed, so they are
 * not read.
 */
const aggregateApyRewards = (rows: readonly ApyRewardRow[]): Rewards[] => {
  const byToken = new Map<string, Rewards>()

  rows.forEach(({ token, apr: rawApr, merklOpportunityUrl }) => {
    const key = getAddress(token.address)
    const apr = getParsedValue(rawApr, safeParseNumber)
    const current = byToken.get(key)

    if (current) {
      byToken.set(key, {
        ...current,
        apr: (current.apr ?? 0n) + (apr ?? 0n),
        merklOpportunityUrl: current.merklOpportunityUrl ?? merklOpportunityUrl ?? undefined
      })
      return
    }

    byToken.set(key, {
      rewardToken: parseApiAsset({
        address: token.address,
        chain: { id: token.chainId },
        symbol: token.symbol ?? '',
        name: token.symbol ?? '',
        decimals: 18,
        logoURI: token.logoUri
      }),
      apr,
      merklOpportunityUrl: merklOpportunityUrl ?? undefined
    })
  })

  return sortingOnKey('apr', Array.from(byToken.values())).filter(
    reward => reward.apr && reward.apr > 0n
  )
}

/** The rewards of a vault APY procedure as hover-card rewards. */
export const normalizeVaultApyRewards = (
  rewards: readonly z.output<typeof vaultApyRewardSchema>[]
): Rewards[] =>
  aggregateApyRewards(
    rewards.map(({ token, supplyApr, merklOpportunityUrl }) => ({
      token,
      apr: supplyApr,
      merklOpportunityUrl
    }))
  )

/**
 * The rewards of one side of a market APY procedure as hover-card rewards. Borrow APRs come
 * out negative, as a reward lowers the borrow rate. Merkl only campaigns a market's borrow
 * side, so supply rows never keep a campaign URL and fall back to their explorer links.
 */
export const normalizeMarketApyRewards = (
  rewards: readonly MarketApyReward[],
  side: 'supply' | 'borrow'
): Rewards[] => {
  const aggregated = aggregateApyRewards(
    rewards.map(({ token, apr, merklOpportunityUrl }) => ({
      token,
      apr,
      merklOpportunityUrl: side === 'borrow' ? merklOpportunityUrl : null
    }))
  )
  return side === 'borrow'
    ? aggregated.map(reward => ({ ...reward, apr: -(reward.apr ?? 0n) }))
    : aggregated
}

/** The collateral rewards of a market APY procedure as collateral-reward rows. */
export const toCollateralRewardEntries = (
  rewards: readonly MarketApyReward[]
): CollateralRewardEntry[] =>
  rewards.map(({ token, apr }) => ({
    asset: {
      chainId: token.chainId,
      address: token.address,
      symbol: token.symbol ?? '',
      logoURI: token.logoUri
    },
    collateralApr: apr
  }))

/**
 * Calculate rewards from market
 *
 * @param options - Options for calculating rewards
 * @param options.market - Market with rewards
 * @param options.type - Type of rewards to calculate
 *
 * @returns Array of rewards
 */
export const rewardsCalculationFromMarket = ({
  market,
  type = 'borrow'
}: {
  market: MarketForRewards | null | undefined
  type: 'borrow' | 'supply'
}) => {
  if (!market) return []

  let marketRewards: RewardsMapByToken

  switch (type) {
    case 'borrow':
      marketRewards = getBorrowRewardsFromMarket(market)
      break
    case 'supply':
      marketRewards = getSupplyRewardsFromMarket(market)
      break
  }

  const sortedRewards = sortingOnKey('apr', Array.from(marketRewards.tokens.values()))

  // Filter out rewards with zero or null APR
  return sortedRewards.filter(reward => reward.apr && reward.apr > 0n)
}

export const computeRewards = ({
  rewardToken,
  apr
}: {
  rewardToken: ApiInputToken
  apr: number | null | undefined
}): Rewards => {
  const correctRewardToken = parseApiAsset(rewardToken)
  const correctApr = getParsedValue(apr, safeParseNumber)

  return {
    apr: correctApr,
    rewardToken: correctRewardToken
  }
}

/**
 * Calculate rewards from vault state rewards
 *
 * @param vaultStateRewards - Array of rewards from vault state
 *
 * @returns Map of rewards with key as reward token address
 */
const getRewardsFromVaultRewards = (
  vaultStateRewards: readonly (MarketRewards | VaultRewards)[] | null | undefined
) => {
  let _rewards = {
    tokens: new Map<string, Rewards>()
  }

  if (!vaultStateRewards) return _rewards

  vaultStateRewards.forEach(reward => {
    const computedRewards = computeRewards({
      rewardToken: reward.asset,
      apr: reward.supplyApr
    })
    const merklOpportunityUrl = 'merklOpportunityUrl' in reward ? reward.merklOpportunityUrl : null
    if (merklOpportunityUrl) computedRewards.merklOpportunityUrl = merklOpportunityUrl

    _rewards = addRewardsPerToken(computedRewards, _rewards)
  })

  return _rewards
}

/**
 * Add borrow rewards per token to rewards map
 *
 * @param reward - Market with rewards
 *
 * @returns Updated rewards map
 */
export const getBorrowRewardsFromMarket = (market: MarketForRewards): RewardsMapByToken => {
  let _rewards = {
    tokens: new Map<string, Rewards>()
  }

  if (!market.state?.rewards) return _rewards

  market.state.rewards.forEach(reward => {
    if (!reward.borrowApr) return
    const marketRewards = computeRewards({
      rewardToken: reward.asset,
      apr: reward.borrowApr
    })

    _rewards = addRewardsPerToken(marketRewards, _rewards)
  })

  return _rewards
}

/**
 * Add supply rewards per token to rewards map
 *
 * @param market - Market with rewards
 *
 * @returns Updated rewards map
 */
const getSupplyRewardsFromMarket = (market: MarketForRewards): RewardsMapByToken => {
  let _rewards = {
    tokens: new Map<string, Rewards>()
  }

  if (!market.state?.rewards) return _rewards

  market.state.rewards.forEach(reward => {
    if (!reward.supplyApr) return
    const marketRewards = computeRewards({
      rewardToken: reward.asset,
      apr: reward.supplyApr
    })

    _rewards = addRewardsPerToken(marketRewards, _rewards)
  })

  return _rewards
}

const addRewardsPerToken = (reward: Rewards, rewards: RewardsMapByToken): RewardsMapByToken => {
  const { rewardToken, apr } = reward
  const addressKey = getAddress(rewardToken.address)

  if (rewards.tokens.has(addressKey)) {
    const current = rewards.tokens.get(addressKey)!

    const totalApr = (() => {
      if (current.apr == null) return apr
      if (apr == null) return current.apr
      return current.apr + apr
    })()

    rewards.tokens.set(addressKey, {
      ...current,
      apr: totalApr,
      merklOpportunityUrl: current.merklOpportunityUrl ?? reward.merklOpportunityUrl
    })
  } else {
    rewards.tokens.set(addressKey, reward)
  }

  return rewards
}

export const getMarketRewards = (
  market: MarketForRewards | null | undefined,
  type: 'supply' | 'borrow' = 'borrow'
): RewardMarket[] => {
  const rewards = rewardsCalculationFromMarket({
    market,
    type
  })

  switch (type) {
    case 'borrow':
      return rewards.map(reward => ({
        ...reward,
        apr: isDefined(reward.apr) ? -reward.apr : null
      }))
    case 'supply':
      return rewards
  }
}
